/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { UserEdition } from "@encoo-web/encoo-lib";
import { Button, Col, Form, Row, Tag } from "antd";
import Modal from "antd/lib/modal/Modal";
import moment from "moment";
import React from "react";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { CompanyList } from "src/models/userManage";

const formItemColor = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  line-height: 14px;
  color: rgb(61, 73, 102);
  font-weight: 600;
`;
const divColor = css`
  margin-top: 12px;
  color: rgb(143, 155, 179);
  background-color: rgb(247, 249, 252);
  padding: 10px 13px;
  min-height: 32px;
`;

export default React.memo(
  (props: {
    isCompanyDetail: boolean;
    companyDetail: CompanyList;
    onClose: () => void;
  }) => {
    const { isCompanyDetail, companyDetail, onClose } = props;
    const formatMessage = useFormatMessage();
    const [form] = Form.useForm();
    const edition = UserEdition[
      companyDetail?.edition as UserEdition
    ] as unknown as number;

    return (
      <div>
        <Modal
          title={formatMessage(messageIds.common.detail)}
          visible={isCompanyDetail}
          width="40%"
          onCancel={onClose}
          footer={""}
          css={css`
            .ant-modal-header {
              border-bottom: none;
            }
          `}
        >
          <Form
            form={form}
            layout="vertical"
            css={css`
              max-height: 80vh;
              overflow-y: auto;
              overflow-x: hidden;
            `}
          >
            <Row gutter={20}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage(
                    messageIds.company.setting.customerIdentifier
                  )}
                  name="id"
                  css={formItemColor}
                >
                  <div css={divColor}>{companyDetail?.companyId}</div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(messageIds.company.companyType)}：`}
                  name="edition"
                  css={formItemColor}
                >
                  <div css={divColor}>
                    {edition === 0
                      ? formatMessage(messageIds.edition.enterprise)
                      : formatMessage(messageIds.edition.community)}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={`${formatMessage(messageIds.company.companyName)}：`}
                  name="name"
                  css={formItemColor}
                >
                  <div css={divColor}>{companyDetail?.name}</div>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={`${formatMessage(messageIds.common.description)}：`}
                  name="description"
                  css={formItemColor}
                >
                  <div css={divColor} style={{ height: "100px" }}>
                    {companyDetail?.description}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(messageIds.common.createdBy)}：`}
                  name="createdByName"
                  css={formItemColor}
                >
                  <div css={divColor}>{companyDetail?.createdByName}</div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(messageIds.common.creationTime)}：`}
                  name="createdAt"
                  css={formItemColor}
                >
                  <div css={divColor}>
                    {moment(companyDetail?.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={formatMessage(
                    messageIds.company.isFixedPostfixMailbox
                  )}
                  name="shouldVerifyDomain"
                  css={formItemColor}
                >
                  <div css={divColor}>
                    {companyDetail?.shouldVerifyDomain === true
                      ? formatMessage(messageIds.common.yes)
                      : formatMessage(messageIds.common.no)}
                  </div>
                </Form.Item>
              </Col>
            </Row>
            {companyDetail?.shouldVerifyDomain ? (
              <Row>
                <Col span={24}>
                  <Form.Item
                    label={`${formatMessage(
                      messageIds.company.mailboxPostfixConfiguration
                    )}：`}
                    name="suffix"
                    css={formItemColor}
                  >
                    <div
                      css={css`
                        flex: auto;
                        max-width: 100%;
                      `}
                    >
                      <div
                        css={css`
                          margin-top: 12px;
                          padding: 4px 10px;
                          min-height: 32px;
                          color: rgb(143, 155, 179);
                          background-color: rgb(247, 249, 252);
                        `}
                      >
                        {companyDetail?.domains
                          ? companyDetail?.domains.map((item) => (
                              <Tag key={item}>{`@${item}`}</Tag>
                            ))
                          : ""}
                      </div>
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              ""
            )}
            <Row>
              <Col span={24}>
                <Form.Item
                  label={`${formatMessage(messageIds.company.companySize)}：`}
                  name="companySize"
                  css={formItemColor}
                >
                  <div css={divColor}>{companyDetail?.companySize}</div>
                </Form.Item>
              </Col>
            </Row>
            <div
              css={css`
                text-align: right;
                margin-top: 20px;
              `}
            >
              <Button
                type="primary"
                css={css`
                  height: 32px;
                  min-width: 88px;
                  border-radius: 4px;
                `}
                onClick={onClose}
              >
                {formatMessage(messageIds.common.confirm)}
              </Button>
            </div>
          </Form>
        </Modal>
      </div>
    );
  }
);
